import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Button } from "reactstrap";
import companyService from "../../../../services/CompanyService";
import permissionsService from "services/permissionsService";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import { PencilSimple } from "@phosphor-icons/react";
import { IconButton, Tooltip } from "@mui/material";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const Companies = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		name: { min: 200 },           // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		cnpj: { min: 200}, // Largura mín/máx da coluna "CNPJ" (em pixels)
		actions: { min: 80, max: 80 }, // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [temPermissaoEditar, setTemPermissaoEditar] = useState(false);
	const [temPermissaoAdicionar, setTemPermissaoAdicionar] = useState(false);
	const [temPermissaoVisualizar, setTemPermissaoVisualizar] = useState(false);

	const navigate = useNavigate();

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	document.title = "Senda | Empresas";

	const columns = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidth('name'),
				minWidthPixels: COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
			},
			{
				header: "CNPJ",
				accessorKey: "cnpj",
				filterFn: "contains",
				widthPixels: getColumnWidth('cnpj'),
				minWidthPixels: COLUMN_WIDTHS.cnpj.min,
				maxWidthPixels: COLUMN_WIDTHS.cnpj.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				widthPixels: getColumnWidth('actions'),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
					}}>
						{temPermissaoEditar && (
							<Tooltip title="Editar" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() => editarItem(row.original)}
								>
									<PencilSimple size={14} />
								</IconButton>
							</Tooltip>
						)}
					</div>
				),
				align: "center",
			},
		],
		[temPermissaoEditar, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const editarItem = (rowData) => {
		navigate(`/empresas/form/${rowData.id}`);
	};

	const getItems = async () => {
		try {
			const response = await companyService.get();
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissions = async () => {
		try {
			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "CompanyUpdate") {
					setTemPermissaoEditar(true);
				}
				if (permission.role === "CompanyCreate") {
					setTemPermissaoAdicionar(true);
				}
				if (permission.role === "CompanyList") {
					setTemPermissaoVisualizar(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		getPermissions();
		getItems();
	}, []);

	if (!temPermissaoVisualizar) return;

	return (
		<div className="page-content">
			{temPermissaoAdicionar && (
				<Breadcrumbs
					botaoLink={"/empresas/form"}
					botao={"Adicionar Empresa"}
					title="Empresas"
					// breadcrumbItem="Empresas"
				/>
			)}

			<div
				className="container-fluid"
				style={{
					backgroundColor: "white",
					padding: 20,
					borderRadius: 10,
					marginTop: 25,
				}}
			>
				<DynamicMaterialTable columns={columns} data={data} />
			</div>
		</div>
	);
};
Companies.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(Companies);
